import { PostTestResults } from 'lcc-interfaces';
import isEqual from 'lodash-es/isEqual';
import merge from 'lodash-es/merge';
import * as moment from 'moment';

import has from '@dojo/framework/core/has';
import { createCommandFactory, createProcess } from '@dojo/framework/stores/process';
import { AddPatchOperation, ReplacePatchOperation } from '@dojo/framework/stores/state/Patch';
import { add, replace } from '@dojo/framework/stores/state/operations';
import { logUser } from '@holmescorp/logger';

import { internalTrackingChange } from './LccStore';
import { userstore as store, userstore } from './base';
import {
	MPEItem,
	MPEItems,
	ParentTree,
	ReadingQueue,
	Recommended,
	SiteStatus,
	ToolState,
	Tracking,
	UserMeta
} from './interfaces';
import { childmap, keymap, pathmap } from './support/loadroots';

window.onpopstate = (evt: PopStateEvent) => {
	console.log('pop history state', evt.state);
	if (evt.state) {
		const { location } = evt.state;
		pathmap.get(location)!.view();
	} else {
		pathmap.get('/explore')!.view();
	}
};

const createCommand = createCommandFactory<any, UserMeta>();

// -----
// Set up the initial values in the store.
const initialStateCommand = createCommand(({ path }) => {
	return [add(path('user_id'), ''), add(path('fullname'), ''), add(path('email'), ''), add(path('rcslocation'), '')];
});

const initialStateProcess = createProcess('initial-userstate', [initialStateCommand]);
initialStateProcess(store)({} as any as UserMeta);

// -----
// Load user state
const loadUserstateCommand = createCommand<{ [key: string]: any }>(({ path, payload }) => {
	const defaults = {
		assessment: {},
		countdown: {},
		explore: { complete: false, extra: null },
		glossary: { complete: false, extra: null },
		guidedtour: {},
		posttest: { complete: false, extra: null },
		practice: {},
		tips: { complete: false, extra: null },
		contentorder: { complete: false, extra: null },
		landing: { complete: false, watched_video: false, examinus: false, survey: {} },
		reading: { complete: false, topic_queues: {} },
		studyplan: {
			complete: false,
			items: [],
			extra: [],
			hasRecommendations: false,
			useML: has('use-ml-study-plan'),
			ml: {
				inFlight: false,
				recommendations: false,
				completedOnce: false
			}
		},
		mlrecommended: { complete: false },
		trackingwylo: { always_resume: false, tracking: {} },
		motivateddate: { date: '', is_show: true, milestone_date: '', is_show_milestone: false }
	};

	if ('toolstate' in payload) {
		payload.toolstate = { ...defaults, ...payload.toolstate };
	}

	if ('toolstate' in payload && 'studyplan' in payload.toolstate) {
		// SInce toolstate overwrites the defaults, we need to ensure that the extra studyplan defaults are present.
		const mlInSP: boolean = 'useML' in payload.toolstate.studyplan;
		payload.toolstate.studyplan.useML = mlInSP ? payload.toolstate.studyplan.useML : has('use-ml-study-plan');
		payload.toolstate.studyplan.ml = mlInSP
			? payload.toolstate.studyplan.ml
			: {
					inFlight: false,
					recommendations: false,
					completedOnce: false
			  };
		payload.toolstate.studyplan.hasRecommendations = mlInSP
			? payload.toolstate.studyplan.hasRecommendations
			: false;
	}

	if ('location' in payload && payload.location === null) {
		payload.location = '/';
	}

	if ('assessment' in payload) {
		// temporarily catch bad assessment data - simple key/values
		const invalidData = Object.keys(payload.assessment).filter((k) => typeof payload.assessment[k] !== 'object');

		invalidData.forEach((key) => {
			console.log(`Bad data in assessment state: ${key}: ${payload.assessment[key]}`);
			delete payload.assessment[key];
		});
	}

	return Object.keys(payload).map((key) => {
		return replace(path(key), payload[key]);
	});
});

export const loadUserstateProcess = createProcess('load-userstate', [loadUserstateCommand]);
export const loadUserstate = loadUserstateProcess(store);

// -----
// Fired on sitestatus.
const fullnameChangeCommand = createCommand<{ sitestatus: SiteStatus }>(({ path, payload: { sitestatus } }) => {
	const { user_id, fullname, email } = sitestatus;
	window.hcstatic.portal_userid = user_id;
	const operations = [
		replace(path('user_id'), user_id),
		replace(path('fullname'), fullname),
		replace(path('email'), email)
	];
	return operations;
});

export const userChangeProcess = createProcess('user-change', [fullnameChangeCommand]);
export const userChange = userChangeProcess(store);

// -----
// Fired on quizPattern change.
const quizPatternChangeCommand = createCommand<{ quizPattern: string }>(({ path, payload: { quizPattern } }) => {
	const operations = [replace(path('quizPattern'), quizPattern)];
	return operations;
});

export const quizPatternChangeProcess = createProcess('quiz-pattern-change', [quizPatternChangeCommand]);
export const quizPatternChange = quizPatternChangeProcess(store);

// -----
// Recommended next
export interface StatusAssessment {
	status: ParentTree;
	assessment: ParentTree;
}

const recommendedChangeCommand = createCommand<StatusAssessment>(({ path, get, payload: { assessment, status } }) => {
	const tgfkeys = Object.keys(assessment).filter((k) => assessment[k].parent !== null);
	const allcompleted: boolean[] = [];
	const recommended: Recommended[] = tgfkeys
		.map((k): Recommended => {
			const [topichead, topicset] = [assessment[k].parent!, k];
			const tskids = childmap.get(topicset)!;
			const topicgroup = tskids[0];
			const basepath = `/explore/${topichead}/${topicset}/${topicgroup}`;
			let title: string = '';
			const tskey = keymap.get(topicset);
			if (tskey) {
				const { title: tstitle } = tskey;
				title = tstitle;
			}

			let isactive = false;
			const iscompleted: boolean[] = [];

			tskids.forEach((tgroup) => {
				const { data } = status[tgroup];
				isactive = isactive || data.complete > 0 || data.percent > 0;
				iscompleted.push(data.percent >= 0.8);
			});
			const completed = iscompleted.length > 0 && iscompleted.every((v) => v);
			allcompleted.push(completed);

			const { percent } = assessment![k].data;
			return {
				topichead,
				topicset,
				topicgroup,
				title,
				basepath,
				percent,
				isactive,
				completed
			};
		})
		.filter((item) => !item.completed)
		.sort((l, r) => l.percent - r.percent)
		.slice(0, 3);

	if (recommended.length === 0) {
		if (allcompleted.length > 0 && allcompleted.every((v) => v)) {
			// a dummy record to flag full completion
			recommended.push({
				topichead: '',
				topicset: '',
				topicgroup: '',
				title: '',
				basepath: '',
				percent: 0.0,
				isactive: false,
				completed: true
			});
		} else {
			// a dummy record to redirect to assessment
			recommended.push({
				topichead: 'assessment',
				topicset: '',
				topicgroup: '',
				title: 'Take the Initial Assessment',
				basepath: '/assessment',
				percent: 0.0,
				isactive: false,
				completed: false
			});
		}
	}

	const current: Recommended[] = get(path('recommended'));
	if (!current) {
		return [add(path('recommended'), recommended)];
	}
	if (!isEqual(current, recommended)) {
		return [replace(path('recommended'), recommended)];
	}
	return [];
});

interface RecommendedTransformerProperties {
	assessment?: ParentTree;
	status?: ParentTree;
}

const recommendedTransformer = (r: RecommendedTransformerProperties): StatusAssessment => {
	const { get, path } = userstore;
	if (r.assessment === undefined) {
		r.assessment = get(path('assessment'));
	}
	if (r.status === undefined) {
		r.status = get(path('status'));
	}
	return { status: r.status, assessment: r.assessment };
};

export const recommendedChangeProcess = createProcess('recommended-change', [recommendedChangeCommand]);
export const recommendedChange = recommendedChangeProcess(store, recommendedTransformer);

const studyPlanToolChangeCommand = createCommand<{
	complete?: boolean;
	items?: any[];
	extra?: any[];
	ml?: { [key: string]: any };
}>(({ path, get, payload }) => {
	const current = get(path('toolstate', 'studyplan'));
	const payloadHasItems = payload.items && Boolean(payload.items.length);
	const currentHasItems = current.hasRecommendations || (current.items && Boolean(current.items.length));
	const { useML } = current;
	if (useML) {
		const ml = current.ml || { inFlight: false, recommendations: false, completedOnce: false };
		ml.recommendations = payloadHasItems ? true : ml.completedOnce ? currentHasItems : false;
		current.hasRecommendations = ml.recommendations;
		ml.completedOnce = ml.completedOnce || true;
		payload.ml = { ...ml, ...payload.ml };
	}
	const updated = { ...current, ...payload };
	return [replace(path('toolstate', 'studyplan'), updated)];
});

export const studyPlanToolChangeProcess = createProcess('studyplan-tool-change', [studyPlanToolChangeCommand]);
export const studyPlanToolChange = studyPlanToolChangeProcess(store);

// -----
// Status
const statusAddCommand = createCommand<{ status: ParentTree }>(({ path, payload: { status } }) => {
	return [add(path('status'), status)];
});

export const statusAddProcess = createProcess('status-add', [statusAddCommand]);
export const statusAdd = statusAddProcess(store);

const statusChangeCommand = createCommand<StatusAssessment>(({ path, get, payload: { status } }) => {
	const current: ParentTree = get(path('status'));
	// const assessment: ParentTree = get(path('assessment'));
	const result = [];
	if (!current) {
		result.push(add(path('status'), status));
	} else {
		const updated = merge({}, current, status);
		result.push(replace(path('status'), updated));
	}
	return result;
});

export const statusChangeProcess = createProcess('status-change', [statusChangeCommand, recommendedChangeCommand]);
export const statusChange = statusChangeProcess(store, recommendedTransformer);

// -----
// Toolstate (completions)
const toolstateCompleteCommand = createCommand<{ completions: string[] }>(({ path, get, payload: { completions } }) => {
	const result: ReplacePatchOperation<any, any>[] = [];
	for (const key of completions) {
		const tool = get(path('toolstate', key));
		if (!tool.complete) {
			tool.complete = true;
			result.push(replace(path('toolstate', key), tool));
		}
	}
	return result;
});

export const toolstateCompleteProcess = createProcess('toolstate-complete', [toolstateCompleteCommand]);
export const toolstateComplete = toolstateCompleteProcess(store);

const toolstateIncompleteCommand = createCommand<{ completions: string[] }>(
	({ path, get, payload: { completions } }) => {
		const result: ReplacePatchOperation<any, any>[] = [];
		for (const key of completions) {
			const tool = get(path('toolstate', key));
			tool.complete = false;
			result.push(replace(path('toolstate', key), tool));
		}
		return result;
	}
);

export const toolstateIncompleteProcess = createProcess('toolstate-incomplete', [toolstateIncompleteCommand]);
export const toolstateIncomplete = toolstateIncompleteProcess(store);

const toolstateCheckCommand = createCommand<{ completions: string[] }>(({ path, get, payload: { completions } }) => {
	const toolstate = get(path('toolstate'));
	const result: AddPatchOperation<any, any>[] = [];
	for (const key of completions) {
		if (toolstate[key] === undefined) {
			const tool = { complete: false, extra: null };
			result.push(add(path('toolstate', key), tool));
		}
	}
	return result;
});

export const toolstateCheckProcess = createProcess('toolstate-check', [toolstateCheckCommand]);
export const toolstateCheck = toolstateCheckProcess(store);

// ONLY FOR USE ON change-tracking
const toolstateChangeCommand = createCommand<{ toolstate: ToolState }>(({ path, payload }) => {
	return [replace(path('toolstate'), payload.toolstate)];
});

export const toolstateChangeProcess = createProcess('toolstate-change', [toolstateChangeCommand]);
export const toolstateChange = toolstateChangeProcess(store);

// -----
// Assessment
const assessmentChangeCommand = createCommand<{ assessment: ParentTree }>(({ path, get, payload: { assessment } }) => {
	const current: ParentTree = get(path('assessment'));
	const toolstate = get(path('toolstate'));
	const result = [];

	if (!toolstate.assessment) {
		toolstate.assessment = { complete: false, extra: null };
	}
	toolstate.assessment.complete = true;

	if (!current) {
		result.push(add(path('assessment'), assessment));
	} else {
		const updated = merge({}, current, assessment);
		if (!isEqual(current, updated)) {
			result.push(replace(path('assessment'), updated));
		}
	}
	if (!isEqual(toolstate.assessment, get(path('toolstate', 'assessment')))) {
		result.push(replace(path('toolstate', 'assessment'), toolstate.assessment));
	}
	return result;
});

export const assessmentChangeProcess = createProcess('assessment-change', [assessmentChangeCommand]);
export const assessmentChange = assessmentChangeProcess(store);

// -----
// Post-test
const posttestChangeCommand = createCommand<{ posttest: PostTestResults }>(({ path, get, payload: { posttest } }) => {
	const current: PostTestResults = get(path('posttest'));
	if (!current) {
		return [add(path('posttest'), posttest)];
	}
	const updated = merge({}, current, posttest);
	if (!isEqual(current, updated)) {
		return [replace(path('posttest'), updated)];
	}
	return [];
});

export const posttestChangeProcess = createProcess('posttest-change', [posttestChangeCommand]);
export const posttestChange = posttestChangeProcess(store);

// -----
// Practice test
const practiceChangeCommand = createCommand<{ practice: ParentTree }>(({ path, get, payload: { practice } }) => {
	const current: ParentTree = get(path('practice'));
	const toolstate = get(path('toolstate'));
	const result = [];

	if (!toolstate.practice) {
		toolstate.practice = { complete: false, extra: null };
	}
	toolstate.practice.complete = true;

	if (!current) {
		result.push(add(path('practice'), practice));
	} else {
		const updated = merge({}, current, practice);
		if (!isEqual(current, updated)) {
			result.push(replace(path('practice'), updated));
		}
		if (!isEqual(toolstate.practice, get(path('toolstate', 'practice')))) {
			result.push(replace(path('toolstate', 'practice'), toolstate.practice));
		}
	}
	return result;
});

export const practiceChangeProcess = createProcess('practice-change', [practiceChangeCommand]);
export const practiceChange = practiceChangeProcess(store);

// -----
// Exam countdown
const examdatePhaseComplete = (current: string | undefined) => {
	// const { get, path } = store;
	// const examScheduled = (() => {
	if (current) {
		return moment(current);
	}
	const earliest = moment().subtract(21, 'days');
	// tslint:disable-next-line:prefer-const
	let [year, month, day] = earliest.toArray();
	if ((month === 11 && day >= 10) || month === 12) {
		year = year + 1;
		month = 1;
	}
	day = 1;
	if (month <= 4) {
		return moment({ year, month: 5, day });
	} else {
		return moment({ year, month: 12, day });
	}
	// })();
	/****
	 * back out toolstate change
	toolstateCheck({ completions: ['explore', 'glossary', 'tips'] });
	const diff = examScheduled.diff(moment(), 'days');
	const toolstate = get(path('toolstate'));
	if (diff <= 21 && !toolstate.glossary.complete) {
		toolstateComplete({ completions: ['explore', 'glossary'] });
	}
	if (diff <= 14 && !toolstate.tips.complete) {
		toolstateComplete({ completions: ['explore', 'glossary', 'tips'] });
	}
	// if exam date is rescheduled, need to allow for resetting complete flags
	if (diff > 14 && toolstate.tips.complete) {
		toolstateIncomplete({ completions: ['tips'] });
	}
	if (diff > 21 && toolstate.glossary.complete) {
		toolstateIncomplete({ completions: ['explore', 'glossary'] });
	}
	*/
};

const examdateChangeCommand = createCommand<{ examdate: string }>(({ path, get, payload: { examdate } }) => {
	const current: string = get(path('examdate'));
	const toolstate = get(path('toolstate'));
	const result = [];

	// round difference up to closest full integer
	const daysuntil = Math.ceil(moment(examdate).diff(moment(), 'days', true));
	toolstate['countdown'].extra = daysuntil >= 1 ? `${daysuntil}` : '\u2014';
	toolstate['countdown'].complete = true;

	if (!current) {
		result.push(add(path('examdate'), examdate));
	} else {
		result.push(replace(path('examdate'), examdate));
	}
	result.push(replace(path('toolstate', 'countdown'), toolstate['countdown']));
	examdatePhaseComplete(examdate);
	return result;
});

export const examdateChangeProcess = createProcess('examdate-change', [examdateChangeCommand]);
export const examdateChange = examdateChangeProcess(store);

export const examdateUpdateCount = (): void => {
	const { get, path } = store;
	const current: string = get(path('examdate'));
	if (current) {
		examdateChange({ examdate: current });
	}
	examdatePhaseComplete(current);
};

const examdateHideCommand = createCommand<{ hide: boolean }>(({ path, get, payload: { hide } }) => {
	// const current: string = get(path('examdate'));
	const toolstate = get(path('toolstate'));
	toolstate['countdown'].hide = hide;

	return [replace(path('toolstate', 'countdown'), toolstate['countdown'])];
});

export const examdateHideProcess = createProcess('examdate-hide', [examdateHideCommand]);
export const examdateHide = examdateHideProcess(store);

// -----
//  Landing Page State
interface Landing {
	complete?: boolean;
	watched_video?: boolean;
	examinus?: boolean;
	survey?: {
		[key: string]: string | object;
	};
}
const landingChangeCommand = createCommand<Landing>(({ path, get, payload }) => {
	const current: Landing = get(path('toolstate', 'landing'));

	const updated = { ...current, ...payload };
	return [replace(path('toolstate', 'landing'), updated)];
});

export const landingChangeProcess = createProcess('landing-change', [landingChangeCommand]);
export const landingChange = landingChangeProcess(store);

const exploreToolChangeCommand = createCommand<{ complete?: boolean; extra?: object }>(({ path, get, payload }) => {
	const current = get(path('toolstate', 'explore'));

	const updated = { ...current, ...payload };
	return [replace(path('toolstate', 'explore'), updated)];
});

export const exploreToolChangeProcess = createProcess('explore-tool-change', [exploreToolChangeCommand]);
export const exploreToolChange = exploreToolChangeProcess(store);

const readingTopicQueueChangeCommand = createCommand<{ key: string; data: ReadingQueue }>(({ path, get, payload }) => {
	const updated = { ...get(path('toolstate', 'reading')) };
	const current: ReadingQueue = updated.topic_queues[payload.key];
	updated.topic_queues[payload.key] = { ...current, ...payload.data };

	return [replace(path('toolstate', 'reading'), updated)];
});

export const readingTopicQueueChangeProcess = createProcess('reading-tool-queue-change', [
	readingTopicQueueChangeCommand
]);
export const readingTopicQueueChange = readingTopicQueueChangeProcess(store);

// -----
//  Guided tour
const guidedtourChangeCommand = createCommand<{ guidedtour: boolean }>(({ path, get, payload: { guidedtour } }) => {
	const toolstate = get(path('toolstate'));
	if (!toolstate.guidedtour) {
		toolstate.guidedtour = { complete: false, extra: null };
	}
	toolstate.guidedtour.complete = guidedtour;

	return [replace(path('toolstate', 'guidedtour'), toolstate.guidedtour)];
});

export const guidedtourChangeProcess = createProcess('guidedtour-change', [guidedtourChangeCommand]);
export const guidedtourChange = guidedtourChangeProcess(store);

// -----
const lastCarouselViewChangeCommand = createCommand<Record<string, unknown>>(({ path, get }) => {
	const visited = moment().toISOString();
	const toolstate = get(path('toolstate'));
	const key = '-last-carousel-visit';
	if (!toolstate[key]) {
		toolstate[key] = { complete: false, extra: visited };
	} else {
		toolstate[key].extra = visited;
	}
	return [replace(path('toolstate', key), toolstate[key])];
});

export const lastCarouselViewChangeProcess = createProcess('last-carousel-view-change', [
	lastCarouselViewChangeCommand
]);
export const carouselVisit = lastCarouselViewChangeProcess(store);

// -----
// Location
const locationChangeCommand = createCommand<{ location: string; popping?: boolean; force?: boolean }>(
	({ path, get, payload: { location, popping = false, force = false } }) => {
		const currLoc = get(path('location'));
		if (force || currLoc !== location) {
			logUser.info(`go to ${location}`);
			if (location === '/') {
				location = '/explore';
			}
			if (location.includes('/explore/')) {
				const [, , topichead, topicset, topicgroup, topic] = location.split('/');
				const tracking = { id: 0, topichead, topicset, topicgroup, topic };
				internalTrackingChange({ tracking });
				trackingWYLOChange({ tracking: { topichead, topicset, topicgroup, topic } });
			}
			if (location.includes('practice')) {
				const topichead = 'practice';
				trackingWYLOChange({ tracking: { topichead } });
			}

			if (!popping) {
				const { sitealias, portal_site } = window.hcstatic;
				window.history.pushState({ location }, '', `/${sitealias || portal_site || ''}${location}`);
			}
			return [replace(path('location'), location)];
		}
		return [];
	}
);

export const locationChangeProcess = createProcess('location-change', [locationChangeCommand]);
export const locationChange = locationChangeProcess(store);

interface WhereYouLeftOff {
	always_resume?: boolean;
	tracking?: Partial<Tracking>;
}

// Tracking where you left of
const trackingWYLOChangeCommand = createCommand<WhereYouLeftOff>(({ path, get, payload }) => {
	const current: WhereYouLeftOff = get(path('toolstate', 'trackingwylo'));
	const updated = { ...current, ...payload };
	return [replace(path('toolstate', 'trackingwylo'), updated)];
});

export const trackingWYLOChangeProcess = createProcess('trackingwylo-change', [trackingWYLOChangeCommand]);
export const trackingWYLOChange = trackingWYLOChangeProcess(store);

// -----
const assessmentScoreCommand = createCommand<{ score: string }>(({ path, get, payload: { score } }) => {
	const toolstate = get(path('toolstate'));
	if (!toolstate.assessment) {
		toolstate.assessment = { complete: true, extra: score };
	} else {
		toolstate.assessment.extra = score;
	}
	return [replace(path('toolstate', 'assessment'), toolstate.assessment)];
});

export const assessmentScoreProcess = createProcess('assessment-score', [assessmentScoreCommand]);
export const assessmentScore = assessmentScoreProcess(store);

// -----
// RCS Location
const rcslocationChangeCommand = createCommand<{ baseurl: string; location: string; popping?: boolean }>(
	({ path, payload: { baseurl, location, popping = false } }) => {
		if (!popping) {
			const { sitealias, portal_site } = window.hcstatic;
			window.history.pushState({ location }, '', `/${sitealias || portal_site || ''}${location}`);
		}
		return [replace(path('rcslocation', baseurl), location)];
	}
);

export const rcslocationChangeProcess = createProcess('rcslocation-change', [rcslocationChangeCommand]);

//
const contentOrderChangeCommand = createCommand<{ contentorder: ToolState }>(({ path, payload }) => {
	return [replace(path('toolstate', 'contentorder'), payload)];
});

export const contentOrderChangeProcess = createProcess('contentorder-change', [contentOrderChangeCommand]);
export const contentOrderChange = contentOrderChangeProcess(store);

//
const mpeChangeCommand = createCommand<{ quizid: string; item?: MPEItem }>(
	({ path, get, payload: { quizid, item } }) => {
		const val = get(path('mpe'));
		if (!val) {
			const newval: MPEItems = {};
			newval[quizid] = item;
			return [add(path('mpe'), newval)];
		}
		if (val[quizid]) {
			return [replace(path('mpe', quizid), item)];
		}
		return [add(path('mpe', quizid), item)];
	}
);

export const mpeChangeProcess = createProcess('mpe-change', [mpeChangeCommand]);
export const mpeChange = mpeChangeProcess(store);

//
const mpeInitCommand = createCommand<{ items: MPEItems }>(({ path, get, payload: { items } }) => {
	const currval = get(path('mpe')) as MPEItems;
	if (currval === undefined) {
		return [add(path('mpe'), items)];
	}
	return [replace(path('mpe'), items)];
});

export const mpeInitProcess = createProcess('mpe-init', [mpeInitCommand]);
export const mpeInit = mpeInitProcess(store);

//
const mpeRemoveCommand = createCommand<{ quizid: string }>(({ path, get, payload: { quizid } }) => {
	const currval = get(path('mpe')) as MPEItems;
	if (currval === undefined) {
		return [];
	}
	if (quizid) {
		delete currval[quizid];
	}
	return [replace(path('mpe'), currval)];
});

export const mpeRemoveProcess = createProcess('mpe-remove', [mpeRemoveCommand]);
export const mpeRemove = mpeRemoveProcess(store);

// -----
//  ML recommended
const mlrecommendedChangeCommand = createCommand<{ mlrecommended: boolean }>(
	({ path, get, payload: { mlrecommended } }) => {
		const toolstate = get(path('toolstate'));
		if (!toolstate.mlrecommended) {
			toolstate.mlrecommended = { complete: false };
		}
		toolstate.mlrecommended.complete = mlrecommended;

		return [replace(path('toolstate', 'mlrecommended'), toolstate.mlrecommended)];
	}
);

export const mlrecommendedChangeProcess = createProcess('mlrecommended-change', [mlrecommendedChangeCommand]);
export const mlrecommendedChange = mlrecommendedChangeProcess(store);

// last motivated date
interface Motivation {
	date?: string;
	is_show?: boolean;
	milestone_date?: string;
	is_show_milestone?: boolean;
}

const motivatedDateChangeCommand = createCommand<Motivation>(({ path, get, payload }) => {
	const current: Motivation = get(path('toolstate', 'motivateddate'));
	const updated = { ...current, ...payload };
	return [replace(path('toolstate', 'motivateddate'), updated)];
});

export const motivatedDateChangeProcess = createProcess('motivateddate-change', [motivatedDateChangeCommand]);
export const motivatedDateChange = motivatedDateChangeProcess(store);
